import { computed, onBeforeUnmount, ref, watch, type Ref } from "vue";
import { useStore } from "vuex";
import placeholderImg from "~/assets/img/placeholder.png";
import { hasRenditions, renditionUrl } from "~/utils/cdn";
import {
  hasProductSpecial,
  resolveProductPrice,
} from "../../../../../utils/productPrice";

type ViewportRefs = {
  isPhoneViewport: Ref<boolean>;
  isTabletViewport: Ref<boolean>;
};

export function useProductStickyBar(
  product: Ref<Record<string, any> | null>,
  loading: Ref<boolean>,
  viewport: ViewportRefs
) {
  const mainBuyEl = ref<HTMLElement | null>(null);
  // Starts true so the bar cannot flash at load on the 1024-1279px two-column
  // layout, where Add to Cart is already on screen; the observer's first
  // callback corrects this before the shopper can act.
  const mainBuyVisible = ref(true);
  let buyObserver: IntersectionObserver | null = null;

  const store = useStore();
  const isCartOpen = computed(() => store.getters["vuefront/sidebarCart"]);

  const showStickyBar = computed(
    () =>
      !loading.value &&
      !!product.value?.id &&
      !product.value?.__productPreview &&
      (viewport.isPhoneViewport.value || viewport.isTabletViewport.value) &&
      !!mainBuyEl.value &&
      !mainBuyVisible.value &&
      !isCartOpen.value
  );

  const stickyDisplayPrice = computed(() =>
    resolveProductPrice(product.value?.special, product.value?.price)
  );
  const stickyHasSpecial = computed(() =>
    hasProductSpecial(product.value?.special, product.value?.price)
  );

  const resolveStickyThumb = (source: unknown) => {
    const imageUrl = String(source ?? "").trim();
    if (!imageUrl) return placeholderImg;
    // A PNG/JPEG from the catalog does not imply that AVIF renditions exist.
    if (!hasRenditions(imageUrl)) return imageUrl;
    return (
      renditionUrl(imageUrl, 600, { ratio: 1, format: "avif" }) || imageUrl
    );
  };

  // Reuse the gallery's cached rendition only for a catalog AVIF source.
  const stickyThumbSrc = ref(resolveStickyThumb(product.value?.image));
  watch(
    () => [product.value?.id, product.value?.image],
    () => {
      stickyThumbSrc.value = resolveStickyThumb(product.value?.image);
    }
  );

  const onStickyThumbError = () => {
    const original = String(product.value?.image ?? "").trim();
    if (
      original &&
      stickyThumbSrc.value !== original &&
      stickyThumbSrc.value !== placeholderImg
    ) {
      stickyThumbSrc.value = original;
      return;
    }
    if (stickyThumbSrc.value !== placeholderImg) {
      stickyThumbSrc.value = placeholderImg;
    }
  };

  const teardownBuyObserver = () => {
    if (buyObserver) {
      buyObserver.disconnect();
      buyObserver = null;
    }
  };

  const setupBuyObserver = () => {
    teardownBuyObserver();
    if (typeof IntersectionObserver === "undefined" || !mainBuyEl.value) {
      // Without an observer the main button's position is unknown; keep the
      // bar up rather than leaving the gallery with no reachable CTA.
      mainBuyVisible.value = false;
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        // A disconnected observer can still deliver queued entries; they
        // describe a button that is no longer the one on the page.
        if (observer !== buyObserver) return;
        // Entries arrive oldest first, so a batch that saw the button enter
        // and leave again ends with its current state.
        const entry = entries[entries.length - 1];
        // The bar stands in for the main Add to Cart whenever that button is
        // off-screen — while the shopper is still on the photos above it as
        // well as after it has scrolled past the top — and steps aside while
        // the real button is on screen so there is never a duplicate CTA.
        if (entry) mainBuyVisible.value = entry.isIntersecting;
      },
      { threshold: 0 }
    );
    buyObserver = observer;
    observer.observe(mainBuyEl.value);
  };

  const setMainBuyRef = (element: Element | any | null) => {
    if (element instanceof HTMLElement) {
      mainBuyEl.value = element;
      setupBuyObserver();
      return;
    }

    mainBuyEl.value = null;
    // Reset, so a remounted button gets the same no-flash start.
    mainBuyVisible.value = true;
    teardownBuyObserver();
  };

  onBeforeUnmount(() => {
    teardownBuyObserver();
  });

  return {
    setMainBuyRef,
    showStickyBar,
    stickyDisplayPrice,
    stickyHasSpecial,
    stickyThumbSrc,
    onStickyThumbError,
  };
}
